import type { ReactNode } from 'react';
import { Map, Pencil } from 'lucide-react';
import { useAorText } from 'utils/aorTerminology';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDateTime } from 'components/IAP/Resources/utils/dateUtils';
import { NULL_VALUE } from 'constants/displayConstants';
import { useAors } from 'hooks/useAors';
import type { ResourceRow } from './ResourceTabCard';
import {
  displayOrNull,
  formatAorName,
  formatAssignees,
  formatCoord,
  formatIncidentName,
  formatPersonsOnBoard,
  formatRequestStatus,
  formatResourceName,
  formatShortId,
  formatSymbology,
  formatUser,
} from './dhsResourceDisplay';
import { formatCheckInStatus, isAssignedToIncident } from './checkInStatus';
import { ResourceThreatChecklistBadges } from './ResourceThreatChecklistBadges';
import { ThreatChecklistCoverageRing } from './ThreatChecklistCoverageRing';

const COLUMNS = [
  'ID',
  'Name',
  'Kind',
  'Type',
  'Asset Status',
  'Asset Status Timestamp',
  'Check-in Status',
  'Quantity',
  'Unit',
  'Hull / Tail Number',
  'Persons on Board',
  'AOR',
  'OPCON',
  'TACON',
  'Incident',
  'Request Status',
  'Owning Org',
  'Address',
  'POC',
  'Assignees',
  'Capabilities',
  'Symbology',
  'Lat',
  'Long',
  'Datetime Ordered',
  'Datetime Ordered Offset',
  'ETA',
  'ETA Offset',
  'Automation rules',
  'Checklists',
  'Notes',
  'Actions',
] as const;

function formatOptionalDate(value: string | undefined): string {
  return value ? formatDateTime(value) : NULL_VALUE;
}

export function DhsResourceTable({
  rows,
  onSelectRow,
  onEditRow,
  onMapClick,
  renderActions,
  resolveTaconLabel,
}: {
  rows: ResourceRow[];
  onSelectRow: (row: ResourceRow) => void;
  onEditRow?: (row: ResourceRow) => void;
  onMapClick?: (row: ResourceRow) => void;
  /** When set, replaces the default Map + Edit action buttons. */
  renderActions?: (row: ResourceRow) => ReactNode;
  /** When set, used for the TACON cell instead of AOR name lookup. */
  resolveTaconLabel?: (taconId: string | undefined) => string;
}) {
  const aorText = useAorText();
  const { data: aors = [] } = useAors();

  return (
    <div className="tw:w-0 tw:min-w-full tw:overflow-x-auto tw:overscroll-x-contain tw:rounded-md tw:border tw:border-border">
      <Table className="tw:min-w-max">
        <TableHeader>
          <TableRow>
            {COLUMNS.map((label) => (
              <TableHead key={label}>{aorText(label)}</TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={COLUMNS.length}
                className="tw:text-xs tw:text-muted-foreground"
              >
                No resources
              </TableCell>
            </TableRow>
          ) : (
            rows.map((row) => (
              <TableRow
                key={row.id}
                role="button"
                tabIndex={0}
                aria-label={`View ${row.name || 'resource'} details`}
                className="tw:cursor-pointer"
                onClick={() => onSelectRow(row)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelectRow(row);
                  }
                }}
              >
                <TableCell>{formatShortId(row)}</TableCell>
                <TableCell>{formatResourceName(row)}</TableCell>
                <TableCell>{displayOrNull(row.kind)}</TableCell>
                <TableCell>{displayOrNull(row.type)}</TableCell>
                <TableCell>{displayOrNull(row.assetStatus)}</TableCell>
                <TableCell>
                  {formatOptionalDate(row.assetStatusTimestamp)}
                </TableCell>
                <TableCell>
                  {formatCheckInStatus(row.status, isAssignedToIncident(row))}
                </TableCell>
                <TableCell>{displayOrNull(row.quantity)}</TableCell>
                <TableCell>{displayOrNull(row.unit)}</TableCell>
                <TableCell>{displayOrNull(row.hull_tail_number)}</TableCell>
                <TableCell>
                  {formatPersonsOnBoard(row.persons_on_board)}
                </TableCell>
                <TableCell>{formatAorName(aors, row.aor)}</TableCell>
                <TableCell>{formatAorName(aors, row.opcon)}</TableCell>
                <TableCell>
                  {resolveTaconLabel
                    ? resolveTaconLabel(row.tacon)
                    : formatAorName(aors, row.tacon)}
                </TableCell>
                <TableCell>{formatIncidentName(row)}</TableCell>
                <TableCell>{formatRequestStatus(row)}</TableCell>
                <TableCell>{displayOrNull(row.owning_organization)}</TableCell>
                <TableCell>{displayOrNull(row.address)}</TableCell>
                <TableCell>{formatUser(row.pointOfContact)}</TableCell>
                <TableCell>{formatAssignees(row.assignees)}</TableCell>
                <TableCell>{displayOrNull(row.capabilities)}</TableCell>
                <TableCell>{formatSymbology(row.symbology)}</TableCell>
                <TableCell>{formatCoord(row.latitude)}</TableCell>
                <TableCell>{formatCoord(row.longitude)}</TableCell>
                <TableCell>{formatOptionalDate(row.datetimeOrdered)}</TableCell>
                <TableCell>
                  {displayOrNull(row.datetimeOrderedOffset)}
                </TableCell>
                <TableCell>{formatOptionalDate(row.eta)}</TableCell>
                <TableCell>{displayOrNull(row.etaTimezoneOffset)}</TableCell>
                <TableCell>
                  <ResourceThreatChecklistBadges
                    threats={row.threats}
                    threat_checklists={row.threat_checklists}
                  />
                </TableCell>
                <TableCell>
                  <ThreatChecklistCoverageRing
                    threats={row.threats}
                    threat_checklists={row.threat_checklists}
                  />
                </TableCell>
                <TableCell>{displayOrNull(row.notes)}</TableCell>
                <TableCell>
                  {renderActions ? (
                    renderActions(row)
                  ) : (
                    <div className="tw:flex tw:items-center tw:gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Show ${row.name || 'resource'} on map`}
                        onClick={(event) => {
                          event.stopPropagation();
                          onMapClick?.(row);
                        }}
                      >
                        <Map />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Edit ${row.name || 'resource'}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          onEditRow?.(row);
                        }}
                      >
                        <Pencil />
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
